import { UserIcon, LockClosedIcon, ArrowLeftIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';
import Footer from '../components/footer/index';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDarkMode } from '../components/darkmode/DarkModeContext';
import ProfileHeader from '../components/Profile/ProfileHeader';
import ProfileForm from '../components/Profile/ProfileForm';
import ProfileSecurity from '../components/Profile/ProfileSecurity';


const ProfilePage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');

  // Auth service class (same as in LoginPage)
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const token = this.getToken();
      
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          if (response.status === 401) {
            // Token expired or invalid, redirect to login
            this.logout();
            throw new Error('Session expired. Please login again.');
          }
          throw new Error(data.message || 'Something went wrong');
        }

        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async updateProfile(profileData) {
      return this.request('/auth/update-profile', {
        method: 'PUT',
        body: JSON.stringify(profileData),
      });
    }

    // Token management
    getToken() {
      return sessionStorage.getItem('auth_token');
    }

    // User data management
    getUserData() {
      const userData = sessionStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    }

    setUserData(userData) {
      sessionStorage.setItem('user_data', JSON.stringify(userData));
    }

    logout() {
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('user_data');
    }
  }

  const authService = new AuthService();

  // Check authentication and load user data
  useEffect(() => {
    const checkAuthAndLoadUser = async () => {
      try {
        const token = authService.getToken();
        const userData = authService.getUserData();

        if (!token || !userData) {
          navigate('/login');
          return;
        }

        setUser(userData);
      } catch (error) {
        console.error('Error loading user data:', error);
        if (error.message.includes('Session expired')) {
          navigate('/login');
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndLoadUser();
  }, [navigate]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleProfileSave = async (profileData) => {
    try {
      // Update profile via API
      const response = await authService.updateProfile(profileData);
      
      if (response.success) {
        // Update local user data
        const updatedUser = { ...user, ...profileData };
        setUser(updatedUser);
        authService.setUserData(updatedUser);
        
        setIsEditing(false);
        
        // Show success message (you might want to use a toast notification)
        alert('Profile berhasil diperbarui!');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error; // Re-throw to be handled by ProfileForm
    }
  };

  const handleProfileCancel = () => {
    setIsEditing(false);
  };

  const tabs = [
    { id: 'profile', name: 'Informasi Profile', icon: <UserIcon className="w-5 h-5" /> },
    { id: 'security', name: 'Keamanan', icon: <LockClosedIcon className="w-5 h-5" /> }
];


  // Loading state
  if (isLoading) {
    return (
      <div className={`min-h-screen ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
      } transition-colors`}>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            {/* Header skeleton */}
            <div className={`${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            } rounded-xl p-6 shadow-sm`}>
              <div className="flex items-center space-x-6">
                <div className="w-24 h-24 bg-gray-300 rounded-full"></div>
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-gray-300 rounded w-1/3"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-300 rounded w-1/4"></div>
                </div>
              </div>
            </div>

            {/* Content skeleton */}
            <div className={`${
              isDarkMode ? 'bg-gray-800' : 'bg-white'
            } rounded-xl p-6 shadow-sm`}>
              <div className="space-y-4">
                <div className="h-6 bg-gray-300 rounded w-1/4"></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="h-10 bg-gray-300 rounded"></div>
                  <div className="h-10 bg-gray-300 rounded"></div>
                  <div className="h-10 bg-gray-300 rounded"></div>
                  <div className="h-10 bg-gray-300 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${
      isDarkMode ? 'bg-gray-900' : 'bg-gray-50'
    } transition-colors`}
    style={{ 
      backgroundImage: `url('https://telnet.net.id/assets/bg.png')`,  // ganti path sesuai file kamu
      backgroundColor: isDarkMode ? '#111827' : '#dfe4e0ff',
      backgroundBlendMode: 'overlay' // opsional biar gambar nyatu dengan warna
    }}
    >
    
        <div className="max-w-4xl mx-auto px-4 py-8 mt-16"
        >
        {/* Page Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Profile Saya
          </h1>
          <p className={`mt-2 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Kelola informasi personal dan pengaturan keamanan akun Anda
          </p>
        </div>

        {/* Profile Header Component */}
        <div className="mb-8">
          <ProfileHeader 
            user={user}
            isEditing={isEditing}
            onEditToggle={handleEditToggle}
          />
        </div>

        {/* Tab Navigation */}
        <div className={`${
          isDarkMode ? 'bg-white border-white' : 'bg-white border-gray-200'
        } rounded-xl border shadow-sm mb-6 overflow-hidden transition-colors`}>
          <div className="flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-all duration-200 ${
                  activeTab === tab.id
                    ? `${
                        isDarkMode 
                          ? 'bg-green-800 text-white border-b-2 border-green-600' 
                          : 'bg-green-700 text-white-900 border-b-2 border-green-700'
                      }`
                    : `${
                        isDarkMode 
                          ? 'text-gray-400 hover:text-white hover:bg-gray-700' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`
                } flex items-center justify-center space-x-2`}
              >
                <span className="text-lg">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'profile' && (
            <ProfileForm
              user={user}
              isEditing={isEditing}
              onSave={handleProfileSave}
              onCancel={handleProfileCancel}
            />
          )}

          {activeTab === 'security' && (
            <ProfileSecurity user={user} />
          )}
        </div>

        {/* Additional Info Card */}
        <div className={`mt-8 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        } rounded-xl p-6 border shadow-sm transition-colors`}>
          <div className="flex items-start space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              isDarkMode ? 'bg-green-800' : 'bg-green-700'
            }`}>
              <svg className={`w-5 h-5 ${
                isDarkMode ? 'text-white' : 'text-blue-800'
              }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <div className="flex-1">
              <h3 className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Tips Keamanan
              </h3>
              <div className={`mt-2 text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-600'
              } space-y-1`}>
                <p>• Pastikan email Anda sudah terverifikasi untuk keamanan maksimal</p>
                <p>• Gunakan password yang kuat dengan kombinasi huruf, angka, dan simbol</p>
                <p>• Jangan bagikan informasi login Anda kepada siapapun</p>
                <p>• Perbarui informasi profile secara berkala</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            ← Kembali ke Dashboard
          </button>

          <div className="flex gap-3">
            <button
              onClick={() => {
                if (window.confirm('Apakah Anda yakin ingin logout?')) {
                  authService.logout();
                  navigate('/login');
                }
              }}
              className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProfilePage;